// Combat scene — two horizontal rows (enemy on top, player on bottom).
// Enemies read from RunState.pendingCombat; player units from combatFighterIds.

import { isPlayerZone } from "../game/RunState";
import { SlotLayout, SLOT_COUNT } from "../engine/SlotLayout";
import type { Scene } from "../engine/scene/SceneManager";
import { drawSprite } from "../engine/render/Sprite";
import type { Game } from "../game/Game";
import { CombatEngine } from "../combat/CombatEngine";
import { CombatAnimator } from "../combat-vfx/CombatAnimator";
import type { MapNode } from "../map/CampaignMap";
import { buildPlayerUnit, applyYuumiAura } from "../combat/UnitFactory";
import type { Unit } from "../units/Unit";
import { effectiveStats, levelForXp } from "../units/UnitStats";
import { routeReward } from "./NodeRouter";
import { MainMenuScene } from "./MainMenuScene";
import { ChampionSelectScene } from "./ChampionSelectScene";
import { TIER_COLORS } from "../data/champions";
import { getAct } from "../data/acts";

const TOP_RESERVED    = 96;   // landscape: space above units
const BOTTOM_RESERVED = 120;  // landscape: space below units
const PORT_TOP        = 56;   // portrait: enemy HP bar area
const PORT_BOTTOM     = 108;  // portrait: player HP bar + buttons

export class CombatScene implements Scene {
  private layout!: SlotLayout;
  private engine!: CombatEngine;
  private animator!: CombatAnimator;
  private endBanner: HTMLDivElement;
  private scoreboardEl: HTMLDivElement | null = null;
  private continueBtn: HTMLButtonElement | null = null;
  private speedBtn: HTMLButtonElement;
  private speedMultiplier = 1;
  private ended = false;
  // When combat resolves we hold the result here and defer the VICTORY/DEFEAT screen
  // until the animator has finished playing every in-flight VFX. Otherwise a kill from
  // a delayed projectile (Jinx rockets, Lux beam, Draven axes) shows the end banner
  // before the rocket lands and the damage number pops.
  private pendingEnd: { winner: "player" | "enemy"; players: Unit[]; since: number } | null = null;
  private readonly PENDING_END_TIMEOUT_MS = 3000;
  private turnTimer = 0;
  private elapsedMs = 0;
  private readonly SUDDEN_DEATH_MS = 120_000;
  private enemyDeathCount = 0;
  private readonly UNIT_DELAY_MS  = 433;  // delay between unit actions within a team
  private readonly TURN_DELAY_MS  = 800;  // delay between teams / end of round
  private playerDisplayedHpRatio = 1;
  private enemyDisplayedHpRatio = 1;
  private unsubResize?: () => void;
  private bgCache: HTMLCanvasElement | null = null;

  constructor(
    private game: Game,
    private node: MapNode,
    private afterCombat?: (winner: "player" | "enemy") => void,
  ) {
    this.computeLayout();
    this.endBanner = document.createElement("div");
    this.endBanner.className = "end-banner";
    this.endBanner.style.display = "none";
    this.speedBtn = document.createElement("button");
    this.speedBtn.className = "combat-speed-btn";
    this.speedBtn.textContent = "3x";
    this.speedBtn.addEventListener("click", () => this.toggleSpeed());
  }

  enter(): void {
    const state = this.game.runState;
    const pending = state?.pendingCombat;
    const players = this.buildPlayers();
    const enemies = pending?.enemies ?? [];

    this.engine = new CombatEngine(players, enemies, this.game.rng);
    this.game.achievements.onCombatStart(this.engine.bus);
    this.animator = new CombatAnimator(this.engine.bus, this.layout, (p, t) => this.engine.applySpinPulse(p, t));
    this.recomputeUnitPositions();
    for (const u of this.engine.units) this.animator.registerUnit(u);
    this.animator.attach();
    this.playerDisplayedHpRatio = this.computeTeamHpRatio("player");
    this.enemyDisplayedHpRatio = this.computeTeamHpRatio("enemy");

    this.game.sceneRoot.appendChild(this.endBanner);
    this.game.sceneRoot.appendChild(this.speedBtn);

    this.engine.bus.on("combat.ended", (e) => { this.pendingEnd = { winner: e.winner, players, since: performance.now() }; });
    this.engine.bus.on("unit.died", (e) => { if (e.unit.team === "enemy") this.enemyDeathCount++; });

    this.unsubResize = this.game.renderer.onResize(() => {
      this.computeLayout();
      this.animator.setLayout(this.layout);
      this.recomputeUnitPositions();
      this.bgCache = null;
    });
  }

  exit(): void {
    this.unsubResize?.();
    this.animator.detach();
    this.endBanner.remove();
    this.scoreboardEl?.remove();
    this.continueBtn?.remove();
    this.speedBtn.remove();
  }

  private toggleSpeed(): void {
    this.speedMultiplier = this.speedMultiplier === 1 ? 3 : 1;
    this.speedBtn.textContent = this.speedMultiplier === 1 ? "3x" : "1x";
    this.speedBtn.classList.toggle("active", this.speedMultiplier > 1);
  }

  update(dt: number): void {
    const scaledDt = dt * this.speedMultiplier;
    if (!this.ended && this.engine.currentPhase === "running") {
      this.elapsedMs += dt;
      if (this.elapsedMs >= this.SUDDEN_DEATH_MS && this.engine.damageMult === 1) {
        this.engine.damageMult = 2;
        this.showSuddenDeathBanner();
      }
      const blocked = this.animator.isBlockingCast();

      if (!blocked) this.turnTimer -= scaledDt;
      if (this.turnTimer <= 0) {
        const pendingDoubleBefore = this.engine.hasPendingDoubleCast;
        const moreUnits = this.engine.executeStep();
        // Give the first blue-buff cast's VFX a full inter-turn pause before the
        // second cast fires, so the two animations don't visually overlap.
        const isDoubleCastPending = !pendingDoubleBefore && this.engine.hasPendingDoubleCast;
        this.turnTimer = !moreUnits ? this.TURN_DELAY_MS
                       : isDoubleCastPending ? this.TURN_DELAY_MS
                       : this.UNIT_DELAY_MS;
      }
    }
    this.animator.update(scaledDt);
    this.updateTeamHpRatios(scaledDt);
    if (this.pendingEnd && !this.ended) {
      const vfxDone = this.animator.isVfxIdle();
      const timedOut = performance.now() - this.pendingEnd.since >= this.PENDING_END_TIMEOUT_MS;
      if (vfxDone || timedOut) {
        if (timedOut && !vfxDone) console.warn("[Combat] pendingEnd timeout — forcing end screen", this.animator.debugState());
        const { winner, players } = this.pendingEnd;
        this.pendingEnd = null;
        this.onCombatEnded(winner, players);
      }
    }
  }

  render(ctx: CanvasRenderingContext2D): void {
    this.drawBackground(ctx);
    this.drawLanes(ctx);
    this.drawYuumiConnection(ctx);
    this.drawUnits(ctx);
    this.animator.drawEffects(ctx);
    this.drawTeamHealthBars(ctx);
  }

  private drawYuumiConnection(ctx: CanvasRenderingContext2D): void {
    const visuals = [...this.animator.unitVisuals];
    const yuumiV = visuals.find(v => !v.dead && v.unit.championDef?.yuumiAura);
    const boostedV = visuals.find(v => !v.dead && v.unit.yuumiBoostMult > 1);
    if (!yuumiV || !boostedV) return;

    const t = performance.now();
    const ax = yuumiV.x + yuumiV.lungeX;
    const ay = yuumiV.y + yuumiV.bobOffset + yuumiV.lungeY;
    const bx = boostedV.x + boostedV.lungeX;
    const by = boostedV.y + boostedV.bobOffset + boostedV.lungeY;

    const pulse  = 0.6 + 0.4 * Math.sin(t * 0.003);
    const pulse2 = 0.6 + 0.4 * Math.sin(t * 0.0045 + 1.2);

    // Bezier control point oscillates perpendicular to the line for an organic wobble.
    const mx = (ax + bx) * 0.5;
    const my = (ay + by) * 0.5;
    const dx = bx - ax;
    const dy = by - ay;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    const perpX = -dy / len;
    const perpY =  dx / len;
    const wobble = Math.sin(t * 0.002) * len * 0.08;
    const cpx = mx + perpX * wobble;
    const cpy = my + perpY * wobble;

    ctx.save();

    // Layer 1 — wide soft outer halo
    ctx.beginPath();
    ctx.moveTo(ax, ay);
    ctx.quadraticCurveTo(cpx, cpy, bx, by);
    ctx.strokeStyle = `rgba(100, 200, 255, ${0.12 * pulse})`;
    ctx.lineWidth = 18;
    ctx.shadowColor = "#66ccff";
    ctx.shadowBlur = 32;
    ctx.setLineDash([]);
    ctx.stroke();

    // Layer 2 — medium inner glow
    ctx.beginPath();
    ctx.moveTo(ax, ay);
    ctx.quadraticCurveTo(cpx, cpy, bx, by);
    ctx.strokeStyle = `rgba(160, 225, 255, ${0.35 * pulse})`;
    ctx.lineWidth = 6;
    ctx.shadowBlur = 16;
    ctx.stroke();

    // Layer 3 — bright animated dashed core
    const grad = ctx.createLinearGradient(ax, ay, bx, by);
    grad.addColorStop(0,    "rgba(180, 230, 255, 0.0)");
    grad.addColorStop(0.15, "rgba(210, 245, 255, 0.9)");
    grad.addColorStop(0.5,  "rgba(245, 252, 255, 1.0)");
    grad.addColorStop(0.85, "rgba(210, 245, 255, 0.9)");
    grad.addColorStop(1,    "rgba(180, 230, 255, 0.0)");
    ctx.beginPath();
    ctx.moveTo(ax, ay);
    ctx.quadraticCurveTo(cpx, cpy, bx, by);
    ctx.strokeStyle = grad;
    ctx.lineWidth = 2;
    ctx.shadowBlur = 10;
    ctx.shadowColor = "#aaddff";
    ctx.setLineDash([8, 5]);
    ctx.lineDashOffset = -(t * 0.05 % 13);
    ctx.stroke();
    ctx.setLineDash([]);

    // Sparkles — 6 motes travelling along the bezier, fading at endpoints
    for (let i = 0; i < 6; i++) {
      const phase = (t * 0.0008 + i / 6) % 1;
      const p = 1 - phase;
      const sx = p * p * ax + 2 * p * phase * cpx + phase * phase * bx;
      const sy = p * p * ay + 2 * p * phase * cpy + phase * phase * by;
      const edgeFade = Math.sin(phase * Math.PI);
      const alpha = 0.75 * edgeFade * (0.45 + 0.55 * Math.sin(t * 0.009 + i * 2.1));
      ctx.beginPath();
      ctx.arc(sx, sy, 2.5, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(210, 248, 255, ${alpha})`;
      ctx.shadowColor = "#88eeff";
      ctx.shadowBlur = 9;
      ctx.fill();
    }

    // Endpoint radial glows
    for (const [ex, ey, a] of [[ax, ay, pulse], [bx, by, pulse2]] as [number, number, number][]) {
      const rg = ctx.createRadialGradient(ex, ey, 0, ex, ey, 12);
      rg.addColorStop(0,   `rgba(230, 248, 255, ${0.9 * a})`);
      rg.addColorStop(0.5, `rgba(160, 220, 255, ${0.4 * a})`);
      rg.addColorStop(1,   "rgba(100, 180, 255, 0)");
      ctx.beginPath();
      ctx.arc(ex, ey, 12, 0, Math.PI * 2);
      ctx.shadowBlur = 0;
      ctx.fillStyle = rg;
      ctx.fill();
    }

    ctx.restore();
  }

  private drawBackground(ctx: CanvasRenderingContext2D): void {
    const actNum = this.game.runState?.act ?? 1;
    const region = getAct(actNum).region;
    const img = this.game.assets.get(`${import.meta.env.BASE_URL}images/map/backgrounds/${region}.jpg`);
    if (!img) return;
    const w = this.game.renderer.designW;
    const h = this.game.renderer.designH;

    if (!this.bgCache) {
      this.bgCache = document.createElement("canvas");
      this.bgCache.width = w;
      this.bgCache.height = h;
      const bCtx = this.bgCache.getContext("2d")!;
      bCtx.filter = "brightness(0.3) saturate(0.4)";
      bCtx.drawImage(img, 0, 0, w, h);
      bCtx.filter = "none";
      bCtx.fillStyle = "rgba(0,0,0,0.35)";
      bCtx.fillRect(0, 0, w, h);
    }

    ctx.drawImage(this.bgCache, 0, 0);
  }

  // ---- Layout ----

  private computeLayout(): void {
    const w = this.game.renderer.designW;
    const h = this.game.renderer.designH;
    const portrait = h > w * 1.15;

    const combatY = portrait
      ? h / 2
      : TOP_RESERVED + (h - TOP_RESERVED - BOTTOM_RESERVED) / 2;

    // Always use landscape side-by-side layout so teams face each other.
    // In portrait the units are smaller but adjacent, which reads better than two distant rows.
    const slotSpacing = (w / 2) / (SLOT_COUNT + 1);
    const unitSize = Math.min(64, Math.max(28, slotSpacing * 0.82));
    this.layout = new SlotLayout(w, combatY, unitSize);
  }

  private recomputeUnitPositions(): void {
    if (!this.animator) return;
    for (const visual of this.animator.unitVisuals) {
      const p = this.layout.hexToPixel(visual.unit.pos);
      visual.x = p.x;
      visual.y = p.y;
    }
  }

  // ---- Unit building ----

  private buildPlayers(): Unit[] {
    const state = this.game.runState;
    if (!state) throw new Error("No RunState");
    const fighterIds = state.combatFighterIds;
    const entries = fighterIds
      ? state.roster.filter((e) => fighterIds.includes(e.id))
      : state.roster;
    const units = entries
      .filter((e) => !(e.currentHp !== null && e.currentHp <= 0))
      .map((entry) => buildPlayerUnit(entry, entry.pos.q, levelForXp(entry.xp)));
    applyYuumiAura(units);
    return units;
  }

  // ---- Rendering ----

  private drawLanes(ctx: CanvasRenderingContext2D): void {
    const w = this.game.renderer.designW;
    const h = this.game.renderer.designH;

    ctx.save();

    if (this.layout.portrait) {
      const midY = this.layout.combatY;
      const sz   = this.layout.size;

      // Enemy zone (above mid)
      const eg = ctx.createLinearGradient(0, 0, 0, midY);
      eg.addColorStop(0,   "rgba(160, 40,  40, 0.14)");
      eg.addColorStop(0.7, "rgba(160, 40,  40, 0.04)");
      eg.addColorStop(1,   "rgba(160, 40,  40, 0.0)");
      ctx.fillStyle = eg;
      ctx.fillRect(0, 0, w, midY);

      // Player zone (below mid)
      const pg = ctx.createLinearGradient(0, midY, 0, h);
      pg.addColorStop(0,   "rgba(40,  80, 160, 0.0)");
      pg.addColorStop(0.3, "rgba(40,  80, 160, 0.04)");
      pg.addColorStop(1,   "rgba(40,  80, 160, 0.14)");
      ctx.fillStyle = pg;
      ctx.fillRect(0, midY, w, h - midY);

      // Glowing centre divider
      const lg = ctx.createLinearGradient(0, 0, w, 0);
      lg.addColorStop(0,   "rgba(200, 200, 200, 0)");
      lg.addColorStop(0.15,"rgba(200, 200, 200, 0.15)");
      lg.addColorStop(0.5, "rgba(220, 220, 255, 0.30)");
      lg.addColorStop(0.85,"rgba(200, 200, 200, 0.15)");
      lg.addColorStop(1,   "rgba(200, 200, 200, 0)");
      ctx.strokeStyle = lg;
      ctx.lineWidth = 1;
      ctx.setLineDash([10, 8]);
      ctx.beginPath();
      ctx.moveTo(0, midY);
      ctx.lineTo(w, midY);
      ctx.stroke();
      ctx.setLineDash([]);

      // Subtle glow pulse on the divider
      ctx.shadowColor = "rgba(180,180,255,0.4)";
      ctx.shadowBlur  = 8;
      ctx.strokeStyle = "rgba(180,180,255,0.12)";
      ctx.lineWidth   = 4;
      ctx.beginPath();
      ctx.moveTo(0, midY);
      ctx.lineTo(w, midY);
      ctx.stroke();
      ctx.shadowBlur = 0;

      // Team labels just inside each zone
      ctx.font = "bold 9px 'Inter', sans-serif";
      ctx.textBaseline = "middle";
      ctx.textAlign = "center";
      ctx.fillStyle = "rgba(200, 80, 80, 0.55)";
      ctx.fillText("ENEMIES",   w / 2, this.layout.enemyY + sz * 2.5);
      ctx.fillStyle = "rgba(80, 140, 200, 0.55)";
      ctx.fillText("YOUR TEAM", w / 2, this.layout.playerY - sz * 2.5);

    } else {
      // Landscape layout
      const cx    = w / 2;
      const laneH = this.layout.size * 3.6;
      const y     = this.layout.combatY;

      // Player zone — left half
      const pg = ctx.createLinearGradient(0, y - laneH / 2, cx, y - laneH / 2);
      pg.addColorStop(0, "rgba(40, 80, 160, 0.0)");
      pg.addColorStop(1, "rgba(60, 120, 200, 0.10)");
      ctx.fillStyle = pg;
      ctx.fillRect(0, y - laneH / 2, cx, laneH);

      // Enemy zone — right half
      const eg = ctx.createLinearGradient(cx, y - laneH / 2, w, y - laneH / 2);
      eg.addColorStop(0, "rgba(180, 60, 60, 0.10)");
      eg.addColorStop(1, "rgba(160, 40, 40, 0.0)");
      ctx.fillStyle = eg;
      ctx.fillRect(cx, y - laneH / 2, cx, laneH);

      // Centre divider
      ctx.strokeStyle = "rgba(220, 220, 255, 0.14)";
      ctx.lineWidth = 1;
      ctx.setLineDash([7, 6]);
      ctx.beginPath();
      ctx.moveTo(cx, TOP_RESERVED);
      ctx.lineTo(cx, h - BOTTOM_RESERVED);
      ctx.stroke();
      ctx.setLineDash([]);

      // Team labels
      ctx.font = "bold 9px 'Inter', sans-serif";
      ctx.textBaseline = "middle";
      ctx.textAlign = "center";
      ctx.fillStyle = "rgba(80, 140, 200, 0.55)";
      ctx.fillText("YOUR TEAM", cx / 2, y + laneH / 2 + 14);
      ctx.fillStyle = "rgba(200, 80, 80, 0.55)";
      ctx.fillText("ENEMIES",   cx + cx / 2, y + laneH / 2 + 14);
    }

    ctx.restore();
  }

  private drawUnits(ctx: CanvasRenderingContext2D): void {
    const visuals = [...this.animator.unitVisuals].sort((a, b) => a.y - b.y);
    const unitSize = this.layout.size * 2;
    for (const visual of visuals) {
      const unit = visual.unit;
      if (visual.dead) continue;

      const img = this.game.assets.get(visual.overrideIconUrl ?? unit.def.iconUrl);
      const px = visual.x + visual.shakeX + visual.lungeX;
      const py = visual.y + visual.bobOffset + visual.lungeY;
      const sy = py - unitSize * 0.15;

      ctx.save();
      ctx.globalAlpha = visual.alpha;

      ctx.save();
      ctx.translate(px, py + unitSize * 0.35);
      ctx.scale(1, 0.35);
      ctx.beginPath();
      ctx.arc(0, 0, unitSize * 0.5, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(0,0,0,0.5)";
      ctx.fill();
      if (unit.blueBuff) {
        ctx.beginPath();
        ctx.arc(0, 0, unitSize * 0.58, 0, Math.PI * 2);
        ctx.strokeStyle = "#3388ff";
        ctx.lineWidth = 2.5;
        ctx.shadowColor = "#3388ff";
        ctx.shadowBlur = 10;
        ctx.stroke();
      }
      if (unit.redBuff) {
        ctx.beginPath();
        ctx.arc(0, 0, unitSize * 0.46, 0, Math.PI * 2);
        ctx.strokeStyle = "#ff5522";
        ctx.lineWidth = 2.5;
        ctx.shadowColor = "#ff5522";
        ctx.shadowBlur = 10;
        ctx.stroke();
      }
      // Vayne Silver Bolts — concentric ground rings, one per stack (innermost first).
      // Rings appear in the order 1→2→3 with a small staggered delay handled by the animator.
      if (visual.vayneVisualStacks > 0) {
        const radii = [unitSize * 0.28, unitSize * 0.42, unitSize * 0.58];
        const count = Math.min(visual.vayneVisualStacks, 3);
        for (let i = 0; i < count; i++) {
          ctx.beginPath();
          ctx.arc(0, 0, radii[i], 0, Math.PI * 2);
          ctx.strokeStyle = "rgba(210,210,210,0.9)";
          ctx.lineWidth   = 3;
          ctx.shadowColor = "#ffffff";
          ctx.shadowBlur  = 10;
          ctx.stroke();
        }
      }
      ctx.restore();

      drawSprite(ctx, img, {
        x: px, y: sy, width: unitSize, height: unitSize,
        pivotX: 0.5, pivotY: 0.5,
        alpha: visual.alpha, scale: visual.scale,
        rotation: visual.rotation,
        circular: true,
        tintColor: visual.hitFlash > 0 ? "#ff2e2e" : visual.healFlash > 0 ? "#22c55e" : undefined,
        tintAmount: visual.hitFlash > 0 ? visual.hitFlash : visual.healFlash,
      });

      if (unit.championDef) {
        const tierColor = TIER_COLORS[unit.championDef.tier];
        const ringColor = unit.stars === 3 ? "#ffe066" : unit.stars === 2 ? "#f0c040" : tierColor;
        const ringWidth = unit.stars >= 2 ? 3.5 : 2.5;
        const ringBlur  = unit.stars >= 2 ? 14  : 6;
        ctx.save();
        ctx.globalAlpha = 0.9 * visual.alpha;
        ctx.strokeStyle = ringColor;
        ctx.lineWidth = ringWidth;
        ctx.shadowColor = ringColor;
        ctx.shadowBlur = ringBlur;
        ctx.beginPath();
        ctx.arc(px, sy, unitSize * 0.52, 0, Math.PI * 2);
        ctx.stroke();
        if (unit.stars === 3) {
          ctx.globalAlpha = 0.4 * visual.alpha;
          ctx.lineWidth = 7;
          ctx.shadowBlur = 20;
          ctx.beginPath();
          ctx.arc(px, sy, unitSize * 0.52, 0, Math.PI * 2);
          ctx.stroke();
        }
        ctx.restore();
      }

      if (visual.alpha > 0.05) {
        ctx.save();
        ctx.fillStyle = "#000";
        ctx.globalAlpha = 0.7 * visual.alpha;
        ctx.fillRect(px - unitSize * 0.45, sy + unitSize * 0.3, 22, 12);
        ctx.globalAlpha = visual.alpha;
        ctx.fillStyle = "#f0e6d2";
        ctx.font = "bold 9px 'Inter', sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(`Lv${unit.level}`, px - unitSize * 0.45 + 11, sy + unitSize * 0.3 + 6);
        ctx.restore();

        if (unit.stars > 1) {
          const starStr = `Lv${unit.level} ${"★".repeat(unit.stars)}`;
          const starColor = unit.stars === 3 ? "#ffe066" : "#f0c040";
          const fontSize = 11;
          const badgeY = sy + unitSize * 0.56;
          ctx.save();
          ctx.globalAlpha = visual.alpha;
          ctx.font = `bold ${fontSize}px 'Inter', sans-serif`;
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          const textW = ctx.measureText(starStr).width;
          const padX = 5, padY = 3;
          const bx = px - textW / 2 - padX;
          const bw = textW + padX * 2;
          const bh = fontSize + padY * 2;
          ctx.fillStyle = "rgba(0,0,0,0.75)";
          ctx.beginPath();
          ctx.roundRect(bx, badgeY - bh / 2, bw, bh, bh / 2);
          ctx.fill();
          ctx.shadowColor = starColor;
          ctx.shadowBlur = 8;
          ctx.fillStyle = starColor;
          ctx.fillText(starStr, px, badgeY);
          ctx.restore();
        }

        if (unit.championDef?.id === "Nasus") {
          const stacks = this.game.runState?.roster.find(e => e.championId === "Nasus")?.nasusStacks ?? 0;
          if (stacks > 0) {
            const stackStr = `${stacks} stacks`;
            const fontSize = 10;
            const badgeY = sy - unitSize * 0.78;
            ctx.save();
            ctx.globalAlpha = visual.alpha;
            ctx.font = `bold ${fontSize}px 'Inter', sans-serif`;
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            const textW = ctx.measureText(stackStr).width;
            const padX = 4, padY = 2;
            const bx = px - textW / 2 - padX;
            const bw = textW + padX * 2;
            const bh = fontSize + padY * 2;
            ctx.fillStyle = "rgba(0,0,0,0.8)";
            ctx.beginPath();
            ctx.roundRect(bx, badgeY - bh / 2, bw, bh, bh / 2);
            ctx.fill();
            ctx.shadowColor = "#e6ac00";
            ctx.shadowBlur = 6;
            ctx.fillStyle = "#e6c200";
            ctx.fillText(stackStr, px, badgeY);
            ctx.restore();
          }
        }

        const bar = this.animator.getHealthBar(unit);
        bar?.draw(ctx, px, sy);

        if (unit.def.abilityId && unit.def.abilityCooldown > 0) {
          this.animator.getCooldownBar(unit)?.draw(ctx, px, sy, visual.alpha);
        }

        if (unit.stunTurnsRemaining > 0) {
          this.drawStunIndicator(ctx, px, sy - unitSize * 0.6, visual.alpha);
        }

        if (unit.stasisTurnsRemaining > 0) {
          ctx.save();
          ctx.globalAlpha = 0.45 * visual.alpha;
          ctx.fillStyle = "#d4aa20";
          ctx.beginPath();
          ctx.arc(px, sy, unitSize * 0.55, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        }

        if (unit.damageReductionTurnsRemaining > 0) {
          ctx.save();
          ctx.globalAlpha = 0.8 * visual.alpha;
          ctx.strokeStyle = "#ffaa20";
          ctx.lineWidth = 3;
          ctx.shadowColor = "#ffaa20";
          ctx.shadowBlur = 16;
          ctx.beginPath();
          ctx.arc(px, sy, unitSize * 0.6, 0, Math.PI * 2);
          ctx.stroke();
          ctx.globalAlpha = 0.12 * visual.alpha;
          ctx.fillStyle = "#ffaa20";
          ctx.beginPath();
          ctx.arc(px, sy, unitSize * 0.6, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        }

        if (unit.olafUltTurnsRemaining > 0) {
          const now2 = Date.now();
          const pulse = 0.65 + 0.35 * Math.sin(now2 * 0.009);
          ctx.save();
          ctx.globalAlpha = 0.9 * pulse * visual.alpha;
          ctx.strokeStyle = "#cc2200";
          ctx.lineWidth = 3.5;
          ctx.shadowColor = "#ff3300";
          ctx.shadowBlur = 22 * pulse;
          ctx.beginPath();
          ctx.arc(px, sy, unitSize * 0.65, 0, Math.PI * 2);
          ctx.stroke();
          ctx.globalAlpha = 0.12 * pulse * visual.alpha;
          ctx.fillStyle = "#ff2200";
          ctx.shadowBlur = 0;
          ctx.beginPath();
          ctx.arc(px, sy, unitSize * 0.65, 0, Math.PI * 2);
          ctx.fill();
          ctx.globalAlpha = 0.55 * visual.alpha;
          ctx.strokeStyle = "#ff7733";
          ctx.lineWidth = 1.5;
          ctx.shadowColor = "#ff5500";
          ctx.shadowBlur = 8;
          for (let i = 0; i < 6; i++) {
            const angle = (now2 * 0.0025) + (i / 6) * Math.PI * 2;
            const inner = unitSize * 0.42;
            const outer = unitSize * 0.7;
            ctx.beginPath();
            ctx.moveTo(px + Math.cos(angle) * inner, sy + Math.sin(angle) * inner);
            ctx.lineTo(px + Math.cos(angle) * outer, sy + Math.sin(angle) * outer);
            ctx.stroke();
          }
          ctx.restore();
        }

        // Sleep indicator — ZZZ floating above the unit
        if (unit.sleepTurnsRemaining > 0) {
          const now = Date.now();
          const float = Math.sin(now * 0.0025) * 4;
          ctx.save();
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          // Purple aura ring
          ctx.globalAlpha = 0.35 * visual.alpha;
          ctx.strokeStyle = "#9944cc";
          ctx.lineWidth   = 2;
          ctx.shadowColor = "#9944cc";
          ctx.shadowBlur  = 14;
          ctx.beginPath();
          ctx.arc(px, sy, unitSize * 0.56, 0, Math.PI * 2);
          ctx.stroke();
          ctx.globalAlpha = 0.08 * visual.alpha;
          ctx.fillStyle   = "#cc88ff";
          ctx.beginPath();
          ctx.arc(px, sy, unitSize * 0.56, 0, Math.PI * 2);
          ctx.fill();
          // Three ZZZs in a rising diagonal
          const zData = [{ s: 9, dx: -5, dy: 0 }, { s: 11, dx: 2, dy: -11 }, { s: 13, dx: 9, dy: -22 }];
          for (const z of zData) {
            ctx.globalAlpha = 0.85 * visual.alpha;
            ctx.font        = `bold ${z.s}px 'Inter', sans-serif`;
            ctx.fillStyle   = "#cc88ff";
            ctx.shadowColor = "#8833aa";
            ctx.shadowBlur  = 10;
            ctx.fillText("Z", px + z.dx, sy - unitSize * 0.72 + z.dy + float);
          }
          ctx.restore();
        }

        // Bleed stack indicator — red badge with stack count
        if (unit.bleedTurnsRemaining > 0 && unit.passiveStacks > 0) {
          const badgeX = px + unitSize * 0.38;
          const badgeY = sy - unitSize * 0.2;
          ctx.save();
          ctx.globalAlpha = 0.9 * visual.alpha;
          ctx.fillStyle   = "rgba(0,0,0,0.75)";
          ctx.beginPath();
          ctx.arc(badgeX, badgeY, 8, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle   = "#cc1100";
          ctx.shadowColor = "#880000";
          ctx.shadowBlur  = 8;
          ctx.beginPath();
          ctx.arc(badgeX, badgeY, 7, 0, Math.PI * 2);
          ctx.fill();
          // Blood droplet tip
          ctx.fillStyle   = "#ee2200";
          ctx.shadowBlur  = 0;
          ctx.beginPath();
          ctx.arc(badgeX, badgeY - 5.5, 2, 0, Math.PI * 2);
          ctx.fill();
          // Stack count
          ctx.fillStyle   = "#ffffff";
          ctx.font        = `bold 8px 'Inter', sans-serif`;
          ctx.textAlign   = "center";
          ctx.textBaseline = "middle";
          ctx.fillText(String(unit.passiveStacks), badgeX, badgeY + 1);
          ctx.restore();
        }

        if (unit.shieldAmount > 0) {
          ctx.save();
          ctx.globalAlpha = 0.7 * visual.alpha;
          ctx.strokeStyle = "#ffd700";
          ctx.lineWidth = 3;
          ctx.shadowColor = "#ffd700";
          ctx.shadowBlur = 10;
          ctx.beginPath();
          ctx.arc(px, sy, unitSize * 0.58, 0, Math.PI * 2);
          ctx.stroke();
          ctx.restore();
        }
      }

      ctx.restore();
    }
  }

  private drawTurnIndicator(ctx: CanvasRenderingContext2D): void {
    if (this.engine.currentPhase !== "running" || this.engine.turnNumber === 0) return;
    const w = this.game.renderer.designW;
    const turn = this.engine.turnNumber;
    const acting = this.engine.actingTeam;
    const isPlayer = acting === "player";
    const teamLabel = isPlayer ? "YOUR TURN" : "ENEMY TURN";
    const accentColor = isPlayer ? "#4a9de8" : "#e85555";

    // In portrait, anchor the badge near the centre divider; in landscape, at the top.
    const portrait = this.layout.portrait;
    const anchorY = portrait ? this.layout.combatY : TOP_RESERVED;

    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    ctx.font = "bold 9px 'Inter', sans-serif";
    ctx.fillStyle = "rgba(160,160,180,0.55)";
    ctx.fillText(`TURN ${turn}`, w / 2, anchorY - (portrait ? 34 : 28));

    ctx.font = "bold 11px 'Inter', sans-serif";
    const textW = ctx.measureText(teamLabel).width;
    const padX = 12, bh = 22;
    const bw = textW + padX * 2;
    const bx = w / 2 - bw / 2;
    const by = anchorY - (portrait ? 34 + bh + 4 : bh + 6);

    ctx.fillStyle = "rgba(8, 10, 20, 0.88)";
    ctx.beginPath();
    ctx.roundRect(bx, by, bw, bh, 4);
    ctx.fill();

    ctx.strokeStyle = accentColor;
    ctx.lineWidth = 1.5;
    ctx.globalAlpha = 0.7;
    ctx.beginPath();
    ctx.roundRect(bx, by, bw, bh, 4);
    ctx.stroke();
    ctx.globalAlpha = 1;

    ctx.shadowColor = accentColor;
    ctx.shadowBlur = 8;
    ctx.fillStyle = accentColor;
    ctx.fillText(teamLabel, w / 2, by + bh / 2);
    ctx.shadowBlur = 0;

    ctx.restore();
  }

  private drawStunIndicator(ctx: CanvasRenderingContext2D, cx: number, cy: number, alpha: number): void {
    const now = Date.now();
    const starCount = 3;
    ctx.save();
    ctx.globalAlpha = alpha;
    for (let i = 0; i < starCount; i++) {
      const angle = now * 0.003 + (i / starCount) * Math.PI * 2;
      const sx = cx + Math.cos(angle) * 9;
      const sy = cy + Math.sin(angle) * 3.5;
      ctx.shadowColor = "#ffd700";
      ctx.shadowBlur = 10;
      ctx.fillStyle = "#ffd700";
      ctx.beginPath();
      for (let p = 0; p < 10; p++) {
        const r = p % 2 === 0 ? 4 : 1.8;
        const a = (p * Math.PI) / 5 - Math.PI / 2;
        if (p === 0) ctx.moveTo(sx + r * Math.cos(a), sy + r * Math.sin(a));
        else ctx.lineTo(sx + r * Math.cos(a), sy + r * Math.sin(a));
      }
      ctx.closePath();
      ctx.fill();
    }
    ctx.restore();
  }

  private computeTeamHpRatio(team: "player" | "enemy"): number {
    const units = this.engine.units.filter((u) => u.team === team);
    const current = units.reduce((s, u) => s + Math.max(0, u.currentHp), 0);
    const max = units.reduce((s, u) => s + effectiveStats(u).maxHp, 0);
    return max > 0 ? current / max : 1;
  }

  private updateTeamHpRatios(dt: number): void {
    const factor = 1 - Math.pow(0.85, dt / 16);
    const targetPlayer = this.computeTeamHpRatio("player");
    const targetEnemy = this.computeTeamHpRatio("enemy");
    this.playerDisplayedHpRatio += (targetPlayer - this.playerDisplayedHpRatio) * factor;
    this.enemyDisplayedHpRatio += (targetEnemy - this.enemyDisplayedHpRatio) * factor;
  }

  private drawTeamHealthBars(ctx: CanvasRenderingContext2D): void {
    const w   = this.game.renderer.designW;
    const h   = this.game.renderer.designH;
    const barH = 10;
    const gap  = 8;

    if (this.layout.portrait) {
      const barW = w - gap * 2;
      // Enemy bar pinned near the top edge
      this.drawHorizontalTeamBar(ctx, gap, 10, barW, barH, this.enemyDisplayedHpRatio, "#c8413d", "ENEMIES");
      // Player bar pinned near the bottom edge
      this.drawHorizontalTeamBar(ctx, gap, h - barH - 10, barW, barH, this.playerDisplayedHpRatio, "#3da35a", "YOUR TEAM");
    } else {
      const barW = w * 0.42;
      const barY = TOP_RESERVED - barH - gap;
      this.drawHorizontalTeamBar(ctx, gap, barY, barW, barH, this.playerDisplayedHpRatio, "#3da35a", "YOUR TEAM");
      this.drawHorizontalTeamBar(ctx, w - barW - gap, barY, barW, barH, this.enemyDisplayedHpRatio, "#c8413d", "ENEMIES");
    }
  }

  private drawHorizontalTeamBar(
    ctx: CanvasRenderingContext2D,
    x: number, y: number, w: number, h: number,
    ratio: number, baseColor: string,
    label: string,
  ): void {
    const r = Math.max(0, Math.min(1, ratio));
    const fillW = w * r;
    const rad = 3;
    const now = Date.now();

    const fillColor = r > 0.5 ? baseColor : r > 0.25 ? "#c87a3d" : "#c8413d";

    ctx.save();

    ctx.font = "bold 8px 'Inter', sans-serif";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "rgba(255,255,255,0.35)";
    ctx.textAlign = "left";
    ctx.fillText(label, x, y - 5);
    ctx.textAlign = "right";
    ctx.fillText(`${Math.round(r * 100)}%`, x + w, y - 5);

    ctx.fillStyle = "#080c12";
    ctx.beginPath();
    ctx.roundRect(x, y, w, h, rad);
    ctx.fill();

    if (fillW > 0) {
      ctx.save();
      ctx.beginPath();
      ctx.roundRect(x, y, w, h, rad);
      ctx.clip();

      ctx.fillStyle = fillColor;
      ctx.fillRect(x, y, fillW, h);

      const glossGrad = ctx.createLinearGradient(x, y, x, y + h);
      glossGrad.addColorStop(0, "rgba(255,255,255,0.28)");
      glossGrad.addColorStop(0.5, "rgba(255,255,255,0.05)");
      glossGrad.addColorStop(1, "rgba(0,0,0,0.25)");
      ctx.fillStyle = glossGrad;
      ctx.fillRect(x, y, fillW, h);

      const t = (now % 2200) / 2200;
      const sx = x - 50 + t * (w + 100);
      const shGrad = ctx.createLinearGradient(sx, y, sx + 55, y);
      shGrad.addColorStop(0, "rgba(255,255,255,0)");
      shGrad.addColorStop(0.5, "rgba(255,255,255,0.2)");
      shGrad.addColorStop(1, "rgba(255,255,255,0)");
      ctx.fillStyle = shGrad;
      ctx.fillRect(x, y, fillW, h);

      ctx.restore();

      if (r < 0.997 && r > 0.003) {
        ctx.save();
        ctx.shadowColor = fillColor;
        ctx.shadowBlur = 10;
        ctx.fillStyle = "rgba(255,255,255,0.92)";
        ctx.fillRect(x + fillW - 1.5, y + 1, 2.5, h - 2);
        ctx.restore();
      }
    }

    ctx.strokeStyle = "rgba(0,0,0,0.45)";
    ctx.lineWidth = 1;
    for (let i = 1; i < 4; i++) {
      const tx = x + (w * i) / 4;
      ctx.beginPath();
      ctx.moveTo(tx, y + 1);
      ctx.lineTo(tx, y + h - 1);
      ctx.stroke();
    }

    ctx.strokeStyle = "rgba(255,255,255,0.08)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.roundRect(x, y, w, h, rad);
    ctx.stroke();

    if (r < 0.3) {
      const pulse = 0.5 + 0.5 * Math.sin(now * 0.005);
      ctx.save();
      ctx.shadowColor = "#ff2020";
      ctx.shadowBlur = 18 * pulse;
      ctx.globalAlpha = 0.7 * pulse;
      ctx.strokeStyle = "#ff2020";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.roundRect(x - 1, y - 1, w + 2, h + 2, rad + 1);
      ctx.stroke();
      ctx.restore();
    }

    ctx.restore();
  }

  private onCombatEnded(winner: "player" | "enemy", players: Unit[]): void {
    if (this.ended) return;
    this.ended = true;
    const isVictory = winner === "player";
    this.endBanner.textContent = isVictory ? "VICTORY" : "DEFEAT";
    this.endBanner.style.display = "block";
    this.syncPlayerHp(players);
    if (isVictory) this.updateNasusStacks(players);
    this.showDamageScoreboard();
    this.game.achievements.onCombatEnd(winner, players, this.node.type, this.game.runState?.roster);

    this.speedBtn.style.display = "none";

    if (isVictory) {
      const btn = document.createElement("button");
      btn.className = "btn combat-continue-btn";
      btn.textContent = "CONTINUE";
      btn.addEventListener("click", () => {
        if (this.afterCombat) { this.afterCombat(winner); return; }
        routeReward(this.game, this.node);
      });
      this.continueBtn = btn;
      this.game.sceneRoot.appendChild(btn);
    } else {
      if (this.afterCombat) {
        setTimeout(() => this.afterCombat!(winner), 1500);
      } else {
        const wasDifficulty = this.game.runState?.difficulty ?? "normal";
        const wrap = document.createElement("div");
        wrap.className = "combat-defeat-actions";

        const newRunBtn = document.createElement("button");
        newRunBtn.className = "btn";
        newRunBtn.textContent = "NEW RUN";
        newRunBtn.addEventListener("click", () => {
          this.game.clearSave();
          this.game.runState = null;
          this.game.startRun(undefined, wasDifficulty);
          this.game.sceneManager.transition(new ChampionSelectScene(this.game));
        });
        wrap.appendChild(newRunBtn);

        const menuBtn = document.createElement("button");
        menuBtn.className = "btn";
        menuBtn.textContent = "MAIN MENU";
        menuBtn.addEventListener("click", () => {
          this.game.clearSave();
          this.game.runState = null;
          this.game.sceneManager.transition(new MainMenuScene(this.game));
        });
        wrap.appendChild(menuBtn);

        this.continueBtn = wrap as unknown as HTMLButtonElement;
        this.game.sceneRoot.appendChild(wrap);
      }
    }
  }

  private updateNasusStacks(players: Unit[]): void {
    const state = this.game.runState;
    if (!state || this.enemyDeathCount === 0) return;
    const nasusPlayer = players.find(p => p.championDef?.id === "Nasus" && p.currentHp > 0);
    if (!nasusPlayer) return;
    const fighterIds = state.combatFighterIds;
    const entries = fighterIds
      ? state.roster.filter(e => fighterIds.includes(e.id))
      : state.roster;
    const nasusEntry = entries.find(e => e.championId === "Nasus");
    if (!nasusEntry) return;
    nasusEntry.nasusStacks = (nasusEntry.nasusStacks ?? 0) + this.enemyDeathCount * 5;
  }

  private showSuddenDeathBanner(): void {
    const el = document.createElement("div");
    el.className = "end-banner sudden-death-banner";
    el.textContent = "⚔ SUDDEN DEATH — 2× DAMAGE";
    this.game.sceneRoot.appendChild(el);
    setTimeout(() => el.remove(), 3000);
  }

  private showDamageScoreboard(): void {
    const units = this.engine.units
      .filter((u) => u.damageDealt > 0)
      .sort((a, b) => b.damageDealt - a.damageDealt);

    const el = document.createElement("div");
    el.className = "damage-scoreboard";

    const header = document.createElement("div");
    header.className = "scoreboard-header";
    header.textContent = "DAMAGE DEALT";
    el.appendChild(header);

    for (const u of units) {
      const row = document.createElement("div");
      row.className = `scoreboard-row ${u.team}`;

      const name = document.createElement("span");
      name.className = "scoreboard-name";
      name.textContent = u.def.displayName;

      const dmg = document.createElement("span");
      dmg.className = "scoreboard-value";
      dmg.textContent = u.damageDealt.toLocaleString();

      row.appendChild(name);
      row.appendChild(dmg);
      el.appendChild(row);
    }

    this.scoreboardEl = el;
    this.game.sceneRoot.appendChild(el);
  }

  private syncPlayerHp(players: Unit[]): void {
    const state = this.game.runState;
    if (!state) return;
    const fighterIds = state.combatFighterIds;
    const entries = fighterIds
      ? state.roster.filter((e) => fighterIds.includes(e.id))
      : state.roster;
    for (let i = 0; i < players.length && i < entries.length; i++) {
      entries[i].currentHp = Math.max(0, Math.round(players[i].currentHp));
    }
  }
}
